import { isPresent, isArray, isString, isDate, isNumeric, isFunction, isBlank } from '../../../utilities/array.util';
//#region Type Definitions
/**
 * Interface for basic filter descriptor
 */
export interface FilterDescriptor {
  readonly field: string;
  readonly value: unknown;
  readonly operator?: string;
  readonly ignoreCase?: boolean;
}

/**
 * Interface for complex filter descriptor with nested filters
 */
export interface ComplexFilterDescriptor {
  readonly logic: 'and' | 'or';
  readonly filters: ReadonlyArray<FilterDescriptor | ComplexFilterDescriptor>;
}

/**
 * Union type for all filter expressions
 */
export type FilterExpression = FilterDescriptor | ComplexFilterDescriptor | ReadonlyArray<FilterDescriptor>;

/**
 * Type for logical operators
 */
type LogicalOperator = {
  readonly concat: (acc: (item: unknown) => boolean, fn: (item: unknown) => boolean) => (item: unknown) => boolean;
  readonly identity: () => boolean;
};

/**
 * Type for filter operator functions
 */
type FilterOperatorFunction = (a: unknown, b?: unknown) => boolean;


/**
 * Type for field getter functions
 */
type FieldGetter = (obj: unknown) => unknown;
//#endregion Type Definitions

//#region Constants and Config

/**
 * Logical operators for combining filters
 */
const LOGICAL_OPERATORS: Record<'and' | 'or', LogicalOperator> = {
  or: {
    concat: (acc: (item: unknown) => boolean, fn: (item: unknown) => boolean) => (item: unknown) => acc(item) || fn(item),
    identity: () => false,
  },
  and: {
    concat: (acc: (item: unknown) => boolean, fn: (item: unknown) => boolean) => (item: unknown) => acc(item) && fn(item),
    identity: () => true,
  },
} as const;

/**
 * Operator mappings for different operator formats
 */
const OPERATOR_MAPPINGS: Readonly<Record<string, string>> = {
  // Standard operators
  '!=': 'neq',
  '<': 'lt',
  '<=': 'lte',
  '==': 'eq',
  '>': 'gt',
  '>=': 'gte',
  
  // Readable operators
  equal: 'eq',
  equals: 'eq',
  equalto: 'eq',
  notequal: 'neq',
  notequals: 'neq',
  notequalto: 'neq',
  greater: 'gt',
  greaterthan: 'gt',
  greaterthanequal: 'gte',
  less: 'lt',
  lessthan: 'lt',
  lessthanequal: 'lte',
  
  // Abbreviated operators
  ge: 'gte',
  le: 'lte',
  ne: 'neq',
  
  // String operators
  notsubstringof: 'doesnotcontain',
  
  // Null/empty operators
  isequalto: 'eq',
  isgreaterthan: 'gt',
  isgreaterthanorequalto: 'gte',
  islessthan: 'lt',
  islessthanorequalto: 'lte',
  isnotequalto: 'neq',
  isempty: 'isempty',
  isnotempty: 'isnotempty',
  isnull: 'isnull',
  isnotnull: 'isnotnull',
} as const;

/**
 * Available filter operators with their implementations
 */
const FILTER_OPERATORS: Readonly<Record<string, FilterOperatorFunction>> = {
  // Equality operators
  eq: (a: unknown, b: unknown): boolean => a === b,
  neq: (a: unknown, b: unknown): boolean => a !== b,
  
  // Comparison operators
  gt: (a: unknown, b: unknown): boolean => {
    if (typeof a === 'number' && typeof b === 'number') return a > b;
    if (typeof a === 'string' && typeof b === 'string') return a > b;
    if (isDate(a) && isDate(b)) return (a as Date).getTime() > (b as Date).getTime();
    return false;
  },
  gte: (a: unknown, b: unknown): boolean => {
    if (typeof a === 'number' && typeof b === 'number') return a >= b;
    if (typeof a === 'string' && typeof b === 'string') return a >= b;
    if (isDate(a) && isDate(b)) return (a as Date).getTime() >= (b as Date).getTime();
    return false;
  },
  lt: (a: unknown, b: unknown): boolean => {
    if (typeof a === 'number' && typeof b === 'number') return a < b;
    if (typeof a === 'string' && typeof b === 'string') return a < b;
    if (isDate(a) && isDate(b)) return (a as Date).getTime() < (b as Date).getTime();
    return false;
  },
  lte: (a: unknown, b: unknown): boolean => {
    if (typeof a === 'number' && typeof b === 'number') return a <= b;
    if (typeof a === 'string' && typeof b === 'string') return a <= b;
    if (isDate(a) && isDate(b)) return (a as Date).getTime() <= (b as Date).getTime();
    return false;
  },
  
  // String operators
  contains: (a: unknown, b: unknown): boolean => {
    const strA = String(a ?? '');
    const strB = String(b ?? '');
    return strA.indexOf(strB) >= 0;
  },
  doesnotcontain: (a: unknown, b: unknown): boolean => {
    const strA = String(a ?? '');
    const strB = String(b ?? '');
    return strA.indexOf(strB) === -1;
  },
  startswith: (a: unknown, b: unknown): boolean => {
    const strA = String(a ?? '');
    const strB = String(b ?? '');
    return strA.lastIndexOf(strB, 0) === 0;
  },
  endswith: (a: unknown, b: unknown): boolean => {
    const strA = String(a ?? '');
    const strB = String(b ?? '');
    return strA.indexOf(strB, strA.length - strB.length) >= 0;
  },
  doesnotstartwith: (a: unknown, b: unknown): boolean => {
    const strA = String(a ?? '');
    const strB = String(b ?? '');
    return strA.lastIndexOf(strB, 0) === -1;
  },
  doesnotendwith: (a: unknown, b: unknown): boolean => {
    const strA = String(a ?? '');
    const strB = String(b ?? '');
    return strA.indexOf(strB, strA.length - strB.length) < 0;
  },
  
  // Empty/null operators
  isempty: (a: unknown): boolean => a === '',
  isnotempty: (a: unknown): boolean => a !== '',
  isnull: (a: unknown): boolean => isBlank(a),
  isnotnull: (a: unknown): boolean => isPresent(a),
  
  // Date operators
  dateIs: (a: unknown, b: unknown): boolean => {
    if (!isDate(a) || !isDate(b)) return false;
    return (a as Date).toDateString() === (b as Date).toDateString();
  },
  dateIsBefore: (a: unknown, b: unknown): boolean => {
    if (!isDate(a) || !isDate(b)) return false;
    return (a as Date).getTime() < (b as Date).getTime();
  },
  dateIsAfter: (a: unknown, b: unknown): boolean => {
    if (!isDate(a) || !isDate(b)) return false;
    return (a as Date).getTime() > (b as Date).getTime();
  },
} as const;

/**
 * Date regex for parsing serialized dates
 */
const DATE_REGEX = /^\/Date\((.*?)\)\/$/;

/**
 * Cache for property getter functions
 */
const getterCache = new Map<string, FieldGetter>();

/**
 * Field regex for parsing nested object paths
 */
const FIELD_REGEX = /\[(?:(\d+)|['"](.*?)['"])\]|((?:(?!\[.*?\]|\.).)+)/g;


/**
 * Type guard to check if value is a Date
 */
function isDateValue(value: unknown): value is Date {
  return isDate(value);
}

/**
 * Type guard to check if value is a string
 */
function isStringValue(value: unknown): value is string {
  return isString(value);
}

/**
 * Type guard to check if value is a function
 */
function isFunctionValue(value: unknown): value is (...args: unknown[]) => unknown {
  return isFunction(value);
}

//#endregion Constants and Config

//#region Utility Functions

/**
 * Normalize operator names to standard format
 */
export function normalizeOperator(operator: string): string {
  if (!operator || !isString(operator)) {
    return 'eq';
  }
  
  const normalized = OPERATOR_MAPPINGS[operator.toLowerCase()];
  return normalized ?? operator.toLowerCase();
}

/**
 * Convert values for comparison (handles dates, case-insensitive strings)
 */
export function convertValue(value: unknown, ignoreCase = false): unknown {
  if (value == null) {
    return value;
  }
  
  if (isStringValue(value)) {
    // Handle serialized dates
    const dateMatch = DATE_REGEX.exec(value);
    if (dateMatch?.[1]) {
      return new Date(Number(dateMatch[1])).getTime();
    }
    
    // Handle case-insensitive strings
    if (ignoreCase) {
      return value.toLowerCase();
    }
  }
  
  if (isDateValue(value)) {
    return value.getTime();
  }
  
  return value;
}

/**
 * Check if filter descriptor is complex (has nested filters)
 */
export function isComplexFilterDescriptor(source: unknown): source is ComplexFilterDescriptor {
  return isPresent(source) && 
         typeof source === 'object' &&
         source !== null &&
         'filters' in source &&
         isArray((source as ComplexFilterDescriptor).filters);
}

/**
 * Check if filter expression is valid
 */
export function isValidFilterExpression(expression: unknown): expression is FilterExpression {
  if (!expression) {
    return false;
  }
  
  if (isArray(expression)) {
    return expression.every((filter: unknown) => isValidFilterDescriptor(filter));
  }
  
  if (isComplexFilterDescriptor(expression)) {
    return expression.filters.every((filter: FilterDescriptor | ComplexFilterDescriptor) => 
      isValidFilterDescriptor(filter) || isComplexFilterDescriptor(filter),
    );
  }
  
  return isValidFilterDescriptor(expression);
}

/**
 * Check if filter descriptor is valid
 */
export function isValidFilterDescriptor(filter: unknown): filter is FilterDescriptor {
  return isPresent(filter) && 
         typeof filter === 'object' &&
         filter !== null &&
         'field' in filter &&
         isString((filter as FilterDescriptor).field) &&
         'value' in filter &&
         (filter as FilterDescriptor).value !== undefined;
}

//#endregion Utility Functions

//#region Getter Functions

/**
 * Create a property getter function for nested object access
 */
export function createGetter(field: string, safe = false): FieldGetter {
  const cacheKey = `${field}_${safe ? 'safe' : 'unsafe'}`;
  
  const cached = getterCache.get(cacheKey);
  if (cached) {
    return cached;
  }
  
  const fieldParts: string[] = [];
  field.replace(FIELD_REGEX, (_match: string, index?: string, indexAccessor?: string, fieldName?: string) => {
    fieldParts.push(isPresent(index) ? index : (indexAccessor ?? fieldName ?? ''));
    return '';
  });
  
  const getter = (obj: unknown): unknown => {
    let result: unknown = obj;
    
    for (const part of fieldParts) {
      if (!isPresent(result) || typeof result !== 'object') {
        return safe ? result : undefined;
      }
      
      result = (result as Record<string, unknown>)[part];
    }
    
    return result;
  };
  
  getterCache.set(cacheKey, getter);
  return getter;
}

/**
 * Create a typed getter that handles type conversion and case sensitivity
 */
export function createTypedGetter(
  fieldGetter: FieldGetter, 
  value: unknown, 
  ignoreCase = false,
): FieldGetter {
  if (!isPresent(value)) {
    return fieldGetter;
  }
  
  if (isStringValue(value)) {
    const dateMatch = DATE_REGEX.exec(value);
    if (dateMatch?.[1]) {
      // Handle date conversion
      return (obj: unknown) => {
        const fieldValue = fieldGetter(obj);
        return isDateValue(fieldValue) ? fieldValue.getTime() : fieldValue;
      };
    } else {
      // Create getter that handles string comparison
      return (obj: unknown) => {
        const fieldValue = fieldGetter(obj);
        if (typeof fieldValue === 'string' && ignoreCase) {
          return fieldValue.toLowerCase();
        }
        return isNumeric(fieldValue) ? String(fieldValue) : fieldValue;
      };
    }
  }
  
  if (isDateValue(value)) {
    return (obj: unknown) => {
      const fieldValue = fieldGetter(obj);
      return isDateValue(fieldValue) ? fieldValue.getTime() : fieldValue;
    };
  }
  
  return fieldGetter;
}

//#endregion Getter Functions


//#region Filter Normalization

/**
 * Normalize a filter expression to a standard complex filter format
 */
export function normalizeFilterDescriptor(descriptor: FilterExpression): ComplexFilterDescriptor {
  if (!descriptor) {
    return { filters: [], logic: 'and' };
  }
  
  if (isArray(descriptor)) {
    const validFilters = descriptor.filter((filter: unknown) => isValidFilterDescriptor(filter)) as FilterDescriptor[];
    return {
      filters: validFilters,
      logic: 'and',
    };
  }
  
  if (isComplexFilterDescriptor(descriptor)) {
    return {
      ...descriptor,
      logic: descriptor.logic ?? 'and',
      filters: descriptor.filters ?? [],
    };
  }
  
  // Single filter descriptor
  if (isValidFilterDescriptor(descriptor)) {
    return {
      filters: [descriptor],
      logic: 'and',
    };
  }
  
  return { filters: [], logic: 'and' };
}

/**
 * Normalize operators in filter descriptors
 */
export function normalizeFilterOperators(descriptor: ComplexFilterDescriptor): ComplexFilterDescriptor {
  if (!descriptor?.filters) {
    return descriptor;
  }
  
  const normalizedFilters = descriptor.filters.map((filter: FilterDescriptor | ComplexFilterDescriptor) => {
    if (isComplexFilterDescriptor(filter)) {
      return normalizeFilterOperators(filter);
    }
    
    if (isValidFilterDescriptor(filter) && isString(filter.operator)) {
      return {
        ...filter,
        operator: normalizeOperator(filter.operator),
      };
    }
    
    return filter;
  });
  
  return {
    ...descriptor,
    filters: normalizedFilters,
  };
}

/**
 * Normalize a complete filter expression
 */
export function normalizeFilters(descriptor: FilterExpression): ComplexFilterDescriptor {
  if (!descriptor) {
    return { filters: [], logic: 'and' };
  }
  
  const normalized = normalizeFilterDescriptor(descriptor);
  return normalizeFilterOperators(normalized);
}

//#endregion Filter Normalization

//#region Filter Compilation

/**
 * Transform a single filter descriptor into a predicate function
 */
export function transformFilter(filter: FilterDescriptor): (item: unknown) => boolean {
  const { field, ignoreCase = true, value, operator = 'contains' } = filter;
  
  // Create field getter
  const fieldGetter: FieldGetter = isFunctionValue(field) 
    ? field as FieldGetter
    : createGetter(field as string, true);
  
  // Create typed getter for proper value comparison
  const typedGetter = createTypedGetter(fieldGetter, value, ignoreCase);
  
  // Convert comparison value
  const convertedValue = convertValue(value, ignoreCase);
  
  // Get operator function
  const operatorFn: FilterOperatorFunction = isFunctionValue(operator) 
    ? operator as FilterOperatorFunction
    : FILTER_OPERATORS[normalizeOperator(operator as string)] ?? FILTER_OPERATORS.eq;
  
  return (item: unknown): boolean => operatorFn(typedGetter(item), convertedValue);
}

/**
 * Transform a complex filter descriptor into a predicate function
 */
export function transformComplexFilter(filter: ComplexFilterDescriptor): (item: unknown) => boolean {
  const { logic = 'and', filters = [] } = filter;
  
  const logicOperator = LOGICAL_OPERATORS[logic];
  if (!logicOperator) {
    throw new Error(`Unsupported logic operator: ${logic}`);
  }
  
  const validFilters = filters.filter((f: FilterDescriptor | ComplexFilterDescriptor) => 
    isValidFilterDescriptor(f) || isComplexFilterDescriptor(f),
  );
  
  if (validFilters.length === 0) {
    return (): boolean => true;
  }
  
  const predicates = validFilters.map((childFilter: FilterDescriptor | ComplexFilterDescriptor) => 
    isComplexFilterDescriptor(childFilter)
      ? transformComplexFilter(childFilter)
      : transformFilter(childFilter),
  );
  
  return predicates.reduce(logicOperator.concat, logicOperator.identity);
}

/**
 * Compile a filter expression into a predicate function
 */
export function compileFilter(descriptor: FilterExpression): (item: unknown) => boolean {
  if (!descriptor) {
    return (): boolean => true;
  }
  
  const normalized = normalizeFilters(descriptor);
  
  if (normalized.filters.length === 0) {
    return (): boolean => true;
  }
  
  return transformComplexFilter(normalized);
}

//#endregion Filter Compilation

//#region Main Filter Function

/**
 * Filter an array of data using the provided filter expression
 */
export function filterBy<T>(data: ReadonlyArray<T>, descriptor: FilterExpression): T[] {
  if (!data || data.length === 0) {
    return [];
  }
  
  if (!descriptor) {
    return [...data];
  }
  
  const normalized = normalizeFilters(descriptor);
  
  if (normalized.filters.length === 0) {
    return [...data];
  }
  
  const filterPredicate = compileFilter(normalized);
  return data.filter((item: T) => filterPredicate(item));
}

//#endregion Main Filter Function

//#region Helper Functions for Common Use Cases

/**
 * Create a simple field filter
 */
export function createFieldFilter(
  field: string, 
  value: unknown, 
  operator = 'contains', 
  ignoreCase = true,
): FilterDescriptor {
  return { field, value, operator, ignoreCase };
}

/**
 * Create a date range filter
 */
export function createDateRangeFilter(
  field: string, 
  startDate?: Date, 
  endDate?: Date,
): ComplexFilterDescriptor {
  const filters: FilterDescriptor[] = [];
  
  if (startDate) {
    filters.push({ field, value: startDate, operator: 'gte' });
  }
  
  if (endDate) {
    filters.push({ field, value: endDate, operator: 'lte' });
  }
  
  return { logic: 'and', filters };
}

/**
 * Create a numeric range filter
 */
export function createNumericRangeFilter(
  field: string, 
  min?: number, 
  max?: number,
): ComplexFilterDescriptor {
  const filters: FilterDescriptor[] = [];
  
  if (min !== undefined && min !== null) {
    filters.push({ field, value: min, operator: 'gte' });
  }
  
  if (max !== undefined && max !== null) {
    filters.push({ field, value: max, operator: 'lte' });
  }
  
  return { logic: 'and', filters };
}

/**
 * Create a multi-value filter (OR logic)
 */
export function createMultiValueFilter(
  field: string, 
  values: ReadonlyArray<unknown>, 
  operator = 'eq',
): ComplexFilterDescriptor {
  const filters = values.map((value: unknown) => ({ field, value, operator }));
  return { logic: 'or', filters };
}

/**
 * Create a text search filter across multiple fields
 */
export function createTextSearchFilter(
  fields: ReadonlyArray<string>, 
  searchTerm: string, 
  operator = 'contains',
): ComplexFilterDescriptor {
  const filters = fields.map((field: string) => ({ 
    field, 
    value: searchTerm, 
    operator, 
    ignoreCase: true,
  }));
  return { logic: 'or', filters };
}

/**
 * Combine multiple filters with AND logic
 */
export function combineFiltersWithAnd(...filters: ReadonlyArray<FilterExpression>): ComplexFilterDescriptor {
  const allFilters: Array<FilterDescriptor | ComplexFilterDescriptor> = [];
  
  for (const filter of filters) {
    if (isArray(filter)) {
      // Type-safe conversion of array to individual filters
      const validFilters = filter.filter((f: unknown) => isValidFilterDescriptor(f)) as FilterDescriptor[];
      allFilters.push(...validFilters);
    } else if (filter) {
      if (isComplexFilterDescriptor(filter) || isValidFilterDescriptor(filter)) {
        allFilters.push(filter);
      }
    }
  }
  
  return { logic: 'and', filters: allFilters };
}

/**
 * Combine multiple filters with OR logic
 */
export function combineFiltersWithOr(...filters: ReadonlyArray<FilterExpression>): ComplexFilterDescriptor {
  const allFilters: Array<FilterDescriptor | ComplexFilterDescriptor> = [];
  
  for (const filter of filters) {
    if (isArray(filter)) {
      // Type-safe conversion of array to individual filters
      const validFilters = filter.filter((f: unknown) => isValidFilterDescriptor(f)) as FilterDescriptor[];
      allFilters.push(...validFilters);
    } else if (filter) {
      if (isComplexFilterDescriptor(filter) || isValidFilterDescriptor(filter)) {
        allFilters.push(filter);
      }
    }
  }
  
  return { logic: 'or', filters: allFilters };
}

//#endregion Helper Functions for Common Use Cases

//#region Debugging and Utility Functions

/**
 * Get a human-readable description of a filter
 */
export function describeFilter(descriptor: FilterExpression): string {
  if (!descriptor) {
    return 'No filter';
  }
  
  if (isArray(descriptor)) {
    if (descriptor.length === 0) {
      return 'No filter';
    }
    if (descriptor.length === 1) {
      const firstFilter = descriptor[0];
      if (isValidFilterDescriptor(firstFilter)) {
        return describeSimpleFilter(firstFilter);
      }
      return 'Invalid filter';
    }
    return `${descriptor.length} filters combined with AND`;
  }
  
  if (isComplexFilterDescriptor(descriptor)) {
    const filterCount = descriptor.filters.length;
    const logic = descriptor.logic.toUpperCase();
    return `${filterCount} filter${filterCount !== 1 ? 's' : ''} combined with ${logic}`;
  }
  
  if (isValidFilterDescriptor(descriptor)) {
    return describeSimpleFilter(descriptor);
  }
  return 'Invalid filter';
}

/**
 * Get a human-readable description of a simple filter
 */
function describeSimpleFilter(filter: FilterDescriptor): string {
  const { field, operator = 'contains', value } = filter;
  const op = normalizeOperator(operator as string);
  
  const operatorDescriptions: Readonly<Record<string, string>> = {
    eq: 'equals',
    neq: 'does not equal',
    gt: 'is greater than',
    gte: 'is greater than or equal to',
    lt: 'is less than',
    lte: 'is less than or equal to',
    contains: 'contains',
    doesnotcontain: 'does not contain',
    startswith: 'starts with',
    endswith: 'ends with',
    isempty: 'is empty',
    isnotempty: 'is not empty',
    isnull: 'is null',
    isnotnull: 'is not null',
  } as const;
  
  const operatorDesc = operatorDescriptions[op] ?? op;
  return `${field} ${operatorDesc} ${String(value)}`;
}

/**
 * Get available operators
 */
export function getAvailableOperators(): ReadonlyArray<string> {
  return Object.keys(FILTER_OPERATORS);
}

/**
 * Check if an operator is valid
 */
export function isValidOperator(operator: string): boolean {
  const availableOperators = getAvailableOperators();
  return availableOperators.includes(normalizeOperator(operator));
}

//#endregion Debugging and Utility Functions


// Legacy compatibility exports (marked as deprecated)
/**
 * @deprecated Use normalizeFilters instead
 */
export const normalizeDescriptor = normalizeFilterDescriptor;

/**
 * @deprecated Use normalizeFilterOperators instead  
 */
export const normalizeOperator_Legacy = normalizeFilterOperators;

/**
 * @deprecated Use transformComplexFilter instead
 */
export const transformCompositeFilter = transformComplexFilter;

/**
 * @deprecated Use isComplexFilterDescriptor instead
 */
export const isCompositeFilterDescriptor = isComplexFilterDescriptor;

/**
 * @deprecated Use createGetter instead
 */
export const getter = createGetter;